(() => {
  if (customElements.get('mfs-cart-gifting')) return;
  class CartGifting extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.root = this.dataset.root || '/';
      this.strings = JSON.parse(this.querySelector('[data-gifting-strings]').textContent);
      this.status = this.querySelector('[data-status]');
      this.toggle = this.querySelector('[data-card-toggle]');
      this.message = this.querySelector('[data-message]');
      this.general = this.form?.querySelector('[data-mfs-general-note] [name="note"]');
      if (this.general) this.general.value = this.dataset.orderNote || '';
      this.revision = 0;
      this.savedRevision = 0;
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.addEventListener('input', event => {
        if (!event.target.matches('[data-message], [data-bag]')) return;
        this.changed(300);
      }, options);
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-card-toggle], [data-card-format], [data-bag], [data-message]')) return;
        this.changed(0);
      }, options);
      this.general?.addEventListener('input', () => this.changed(300), options);
      this.form?.addEventListener('submit', event => this.checkout(event), {capture: true, ...options});
      document.addEventListener('click', event => {
        const link = event.target.closest('a[href]');
        if (link && new URL(link.href, location.href).pathname.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout(?:\/|$)/i)) this.checkout(event, link.href);
      }, {capture: true, ...options});
      this.querySelectorAll('[data-gift-price]').forEach(element => {
        element.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style: 'currency', currency: element.dataset.currency}).format(Number(element.dataset.giftPrice) / 100);
      });
      this.count();
      this.setFields();
      if (this.dataset.savedCard === 'Yes' && !this.handwritten()) this.changed(0);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
    }
    count() {
      const count = this.querySelector('[data-count]');
      if (count && this.message) count.textContent = `${150 - this.message.value.length} ${this.strings.count}`;
    }
    handwritten() {
      return !!this.toggle?.checked && this.querySelector('[data-card-format]:checked')?.value === 'handwritten';
    }
    setFields() {
      const selected = this.handwritten();
      const formats = this.querySelector('[data-card-formats]');
      if (formats) formats.hidden = !this.toggle?.checked;
      this.querySelectorAll('[data-card-image]').forEach(image => { image.hidden = image.dataset.cardImage !== (this.querySelector('[data-card-format]:checked')?.value || 'blank'); });
      const wrap = this.querySelector('[data-message-wrap]');
      if (wrap) wrap.hidden = !selected;
      if (this.message) this.message.disabled = !selected;
      if (this.general) this.general.disabled = selected;
    }
    changed(delay) {
      this.revision++;
      this.error = null;
      this.count();
      this.setFields();
      this.setBusy(true);
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.flush().catch(() => {}), delay);
    }
    setBusy(busy) {
      this.setAttribute('aria-busy', String(busy));
      const table = this.form?.querySelector('.order-summary');
      if (table) table.inert = busy;
      this.form?.querySelectorAll('.additional-checkout-buttons').forEach(element => { element.inert = busy; });
    }
    snapshot() {
      const bags = [...this.querySelectorAll('[data-bag]')];
      const invalid = bags.find(input => !input.checkValidity());
      if (invalid) { invalid.reportValidity(); throw Error(this.strings.error); }
      return {
        card: this.handwritten() ? 1 : 0,
        blank: this.toggle?.checked && !this.handwritten() ? 1 : 0,
        message: this.message?.value.trim() || '',
        general: (this.general ? this.general.value : this.dataset.orderNote || '').trim(),
        bags: bags.map(input => ({id: Number(input.dataset.bag), quantity: Number(input.value)}))
      };
    }
    async request(path, body) {
      const response = await fetch(this.root + path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin',
        headers: body ? {'Content-Type': 'application/json'} : {},
        body: body ? JSON.stringify(body) : undefined, keepalive: true
      });
      let data;
      try { data = await response.json(); } catch { throw Error(this.strings.error); }
      if (!response.ok) throw Error(typeof data.description === 'string' ? data.description : this.strings.error);
      return data;
    }
    flush() {
      clearTimeout(this.timer);
      if (this.work) return this.work;
      this.work = this.drain().finally(() => {
        this.work = null;
        this.setBusy(false);
      });
      return this.work;
    }
    async drain() {
      try {
        let last;
        let itemsChanged = false;
        while (this.savedRevision !== this.revision) {
          const revision = this.revision;
          const desired = this.snapshot();
          this.status.textContent = this.strings.saving;
          this.status.removeAttribute('data-error');
          const cart = await this.request('cart.js');
          const cardId = Number(this.dataset.card);
          const physical = cart.items.some(item => item.requires_shipping && ![Number(this.dataset.cardProduct), Number(this.dataset.bagProduct), Number(this.dataset.blankProduct)].includes(item.product_id));
          if (!physical && (desired.card || desired.blank || desired.bags.some(bag => bag.quantity))) throw Error(this.strings.addSilk);
          const targets = [...desired.bags];
          if (cardId) targets.unshift({id: cardId, quantity: desired.card});
          if (Number(this.dataset.blankCard)) targets.unshift({id: Number(this.dataset.blankCard), quantity: desired.blank});
          for (const target of targets) {
            const lines = cart.items.filter(item => item.variant_id === target.id);
            for (let index = 0; index < lines.length; index++) {
              const quantity = index === 0 ? target.quantity : 0;
              if (lines[index].quantity !== quantity) {
                await this.request('cart/change.js', {id: lines[index].key, quantity});
                itemsChanged = true;
              }
            }
            if (!lines.length && target.quantity) {
              await this.request('cart/add.js', {items: [{id: target.id, quantity: target.quantity}]});
              itemsChanged = true;
            }
          }
          const sections = [this.dataset.section];
          document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {bubbles: true, detail: {sections}}));
          const note = desired.card ? desired.message : desired.general;
          last = await this.request('cart/update.js', {
            note, attributes: {'Gift message': desired.card ? desired.message : '', 'Handwritten card': desired.card ? 'Yes' : '', 'Greeting card': desired.card ? 'Handwritten' : desired.blank ? 'Blank inside' : '', 'Order instructions': desired.general},
            sections: [...new Set(sections)].slice(0, 5), sections_url: location.pathname
          });
          const quantity = id => last.items.filter(item => item.variant_id === id).reduce((sum, item) => sum + item.quantity, 0);
          if (last.note !== note || targets.some(target => quantity(target.id) !== target.quantity)) throw Error(this.strings.changed);
          this.savedRevision = revision;
        }
        if (last && this.isConnected) {
          if (itemsChanged) this.renderCart(last);
          const drawer = document.querySelector('cart-drawer');
          if (!drawer || Object.values(last.sections || {}).every(Boolean)) {
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {bubbles: true, detail: {cart: last, baseEvent: 'gifting:change'}}));
          } else {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true}));
          }
          this.status.textContent = this.strings.saved;
        }
        this.error = null;
      } catch (error) {
        this.error = error;
        this.status.textContent = error.message;
        this.status.setAttribute('data-error', '');
        throw error;
      }
    }
    renderCart(cart) {
      const html = cart.sections?.[this.dataset.section];
      if (!html) throw Error(this.strings.error);
      const next = new DOMParser().parseFromString(html, 'text/html');
      for (const selector of ['.order-summary', '.cart-recap']) {
        const current = this.form.querySelector(selector);
        const replacement = next.querySelector(selector);
        if (!current || !replacement) throw Error(this.strings.error);
        current.replaceWith(replacement);
      }
    }
    async checkout(event, href) {
      if (this.resuming) return;
      const submitter = event.submitter;
      if (!href && submitter && submitter.name !== 'checkout') return;
      if (!href && event.type === 'submit' && !submitter && document.activeElement?.closest('mfs-cart-gifting')) {
        event.preventDefault();
        return;
      }
      if (this.checkingOut) { event.preventDefault(); return; }
      if (this.handwritten() && (!this.message.value.trim() || this.message.value.length > 150)) {
        event.preventDefault();
        this.status.textContent = this.strings.messageRequired;
        this.status.setAttribute('data-error', '');
        this.message.focus();
        return;
      }
      if (this.savedRevision === this.revision && !this.work && !this.error) return;
      event.preventDefault();
      this.checkingOut = true;
      try {
        if (this.error && this.savedRevision === this.revision) this.revision++;
        await this.flush();
        if (this.savedRevision !== this.revision || this.error) throw Error(this.strings.error);
        this.resuming = true;
        if (href) location.assign(href);
        else {
          const checkout = this.form.querySelector('[name="checkout"]');
          if (checkout) this.form.requestSubmit(checkout);
        }
      } catch {
        this.status.focus?.();
      } finally {
        this.checkingOut = false;
      }
    }
  }
  customElements.define('mfs-cart-gifting', CartGifting);
})();
